import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Modal, Button, Alert } from '@components';
import { ApiClient } from '../../../utils/apiClient';

const getStripePublishableKey = () => {
  return (
    (import.meta as { env?: { VITE_STRIPE_PUBLISHABLE_KEY?: string } }).env
      ?.VITE_STRIPE_PUBLISHABLE_KEY || ''
  );
};

const getStripePromise = () => {
  const key = getStripePublishableKey();
  if (!key) {
    console.warn(
      'Stripe publishable key not found. Please set VITE_STRIPE_PUBLISHABLE_KEY in your environment variables.'
    );
    return null;
  }
  return loadStripe(key);
};

const stripePromise = getStripePromise();

interface AddPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  loading?: boolean;
  error?: string | null;
  onAttachPaymentMethod?: (paymentMethodId: string) => Promise<void>;
}

const PaymentMethodForm: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
  onSubmit: (paymentMethodId: string) => Promise<void>;
  loading?: boolean;
  error?: string | null;
}> = ({ onClose, onSuccess, onSubmit, loading = false, error = null }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setFormError(null);

    try {
      const paymentElement = elements.getElement(PaymentElement);
      if (!paymentElement) {
        throw new Error('Payment element not found');
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message || 'Failed to submit payment method');
      }

      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (pmError) {
        throw new Error(pmError.message || 'Failed to create payment method');
      }

      if (!paymentMethod) {
        throw new Error('Payment method creation failed');
      }

      await onSubmit(paymentMethod.id);
      onSuccess();
      onClose();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('billing.failedToAddPaymentMethod'));
    } finally {
      setIsProcessing(false);
    }
  };

  const paymentElementOptions = {
    layout: 'tabs' as const,
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {(error || formError) && (
        <Alert variant="error" title="Error">
          {error || formError}
        </Alert>
      )}

      <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
        <PaymentElement options={paymentElementOptions} />
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        {t('billing.paymentDetailsSecurelyProcessed')}
      </p>

      <div className="flex gap-3 justify-end">
        <Button variant="ghost" onClick={onClose} disabled={isProcessing || loading}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="gradient-multi"
          disabled={!stripe || isProcessing || loading}
          className="flex items-center gap-2"
        >
          {isProcessing || loading ? (
            <>
              <span className="material-icons animate-spin">refresh</span>
              {t('billing.addingPaymentMethod')}
            </>
          ) : (
            <>
              <span className="material-icons">save</span>
              {t('billing.addPaymentMethod')}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  loading = false,
  error = null,
  onAttachPaymentMethod,
}) => {
  const { t } = useTranslation();
  const handleSubmit = async (paymentMethodId: string) => {
    if (onAttachPaymentMethod) {
      await onAttachPaymentMethod(paymentMethodId);
    } else {
      const response = await ApiClient.post('/billing/payment-methods/attach/', {
        payment_method_id: paymentMethodId,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to attach payment method');
      }
    }
  };

  if (!stripePromise) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={t('billing.addPaymentMethod')} size="md">
        <Alert variant="error" title="Configuration Error">
          Stripe is not configured. Please set VITE_STRIPE_PUBLISHABLE_KEY in your environment
          variables.
        </Alert>
      </Modal>
    );
  }

  const elementsOptions = {
    mode: 'setup' as const,
    paymentMethodCreation: 'manual' as const,
    paymentMethodTypes: ['card', 'link'],
    appearance: {
      theme: 'stripe' as const,
    },
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('billing.addPaymentMethod')} size="md">
      <Elements stripe={stripePromise} options={elementsOptions}>
        <PaymentMethodForm
          onClose={onClose}
          onSuccess={onSuccess}
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
        />
      </Elements>
    </Modal>
  );
};
